import {
  collection,
  doc,
  addDoc,
  updateDoc,
  getDoc,
  getDocs,
  query,
  where,
  serverTimestamp,
  arrayUnion,
  arrayRemove,
  Timestamp,
} from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { app, db } from '@/config/firebase';
import { uploadFile } from '@/utils/storage';
import {
  EquipamentoCreateSchema,
  EquipamentoUpdateSchema,
  AlteracaoSituacaoAtivoSchema,
} from '../models/equipamento.schemas';
import { normalizarPatrimonio } from '../models/equipamento.utils';
import type { AuditoriaAtor } from '../models/solicitacao.types';
import type {
  AnexoAtivo,
  CategoriaAnexoAtivo,
  AlteracaoSituacaoAtivo,
  ExclusaoAtivo,
  ComentarioAtivo,
  Equipamento,
  EquipamentoCreate,
  EquipamentoUpdate,
  StatusCustodiaEquipamento,
} from '../models/equipamento.types';

/**
 * Operações Firestore para si_equipamentos — exclusivo do SI (ver firestore.rules). É a
 * ficha do ativo do inventário próprio (discovery/SI/02-INVENTARIO-PROPRIO-FASE1.md).
 */
/**
 * Autoria de toda escrita feita pelo app — lida pela Cloud Function do histórico
 * (onEquipamentoWriteHistorico), que só confia nela quando muda na mesma escrita. Usa o
 * ator passado (ficha de Ativos) ou, na falta, o usuário logado (vínculo em Movimentação,
 * Estadia em Cliente...), pra nenhuma escrita do app ficar sem autor.
 */
function ultimaAlteracaoManual(ator?: AuditoriaAtor | null) {
  const usuario = getAuth(app).currentUser;
  const autor = ator ?? (usuario ? { id: usuario.uid, name: usuario.displayName ?? usuario.email ?? '' } : null);
  return { tipo: 'manual' as const, ator: autor ? { id: autor.id, name: autor.name } : null, em: serverTimestamp() };
}

class EquipamentoServiceClass {
  private collectionName = 'si_equipamentos';

  private paraEquipamento(snap: { id: string; data: () => unknown }): Equipamento {
    return { id: snap.id, ...(snap.data() as object) } as Equipamento;
  }

  async create(data: EquipamentoCreate): Promise<string> {
    const validated = EquipamentoCreateSchema.parse(data);

    const docRef = await addDoc(collection(db, this.collectionName), {
      ...validated,
      ...(validated.patrimonio !== undefined ? { patrimonio: normalizarPatrimonio(validated.patrimonio) } : {}),
      statusCustodia: validated.statusCustodia ?? 'estoque_raro',
      ultimaAlteracao: ultimaAlteracaoManual(validated.createdBy),
      // Criado com o histórico já existindo — não precisa buscar passado (pendência).
      historicoDesdeCriacao: true,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });

    return docRef.id;
  }

  async update(data: EquipamentoUpdate): Promise<void> {
    const validated = EquipamentoUpdateSchema.parse(data);
    const { id, ...fields } = validated;

    await updateDoc(doc(db, this.collectionName, id), {
      ...fields,
      ...(fields.patrimonio !== undefined ? { patrimonio: normalizarPatrimonio(fields.patrimonio) } : {}),
      ultimaAlteracao: ultimaAlteracaoManual(fields.updatedBy),
      updatedAt: serverTimestamp(),
    });
  }

  /** Alteração MANUAL de Situação — motivo obrigatório; autor/data ficam na ficha (e, na
   * fase 2b, no histórico). As automáticas vêm das Cloud Functions de sync. */
  async alterarSituacao(data: AlteracaoSituacaoAtivo): Promise<void> {
    const validated = AlteracaoSituacaoAtivoSchema.parse(data);
    await updateDoc(doc(db, this.collectionName, validated.id), {
      statusCustodia: validated.statusCustodia,
      situacaoDetalhe: validated.situacaoDetalhe ?? null,
      // Só vale em "Em manutenção" — qualquer outra Situação limpa.
      fornecedorManutencao:
        validated.statusCustodia === 'em_manutencao' ? validated.fornecedorManutencao?.trim() || null : null,
      situacaoMotivo: validated.motivo,
      situacaoAlteradaPor: validated.ator,
      situacaoAlteradaEm: serverTimestamp(),
      updatedBy: validated.ator,
      ultimaAlteracao: ultimaAlteracaoManual(validated.ator),
      updatedAt: serverTimestamp(),
    });
  }

  /**
   * Exclusão lógica — o registro fica (Movimentações/Termos/histórico apontam pra ele). Quem
   * chama já conferiu que o ativo está livre (podeExcluirAtivo); o histórico registra o motivo.
   */
  async excluir(data: ExclusaoAtivo): Promise<void> {
    const motivo = data.motivo?.trim() || null;
    if (data.motivoTipo === 'outro' && !motivo) throw new Error('Informe o motivo da exclusão.');
    await updateDoc(doc(db, this.collectionName, data.id), {
      excluido: true,
      motivoExclusaoTipo: data.motivoTipo,
      motivoExclusao: motivo,
      excluidoPor: data.ator,
      excluidoEm: serverTimestamp(),
      updatedBy: data.ator,
      ultimaAlteracao: ultimaAlteracaoManual(data.ator),
      updatedAt: serverTimestamp(),
    });
  }

  async restaurar(id: string, ator: AuditoriaAtor): Promise<void> {
    await updateDoc(doc(db, this.collectionName, id), {
      excluido: false,
      updatedBy: ator,
      ultimaAlteracao: ultimaAlteracaoManual(ator),
      updatedAt: serverTimestamp(),
    });
  }

  /** `arrayUnion` não aceita serverTimestamp dentro do item — usa o horário do client. */
  async adicionarComentario(id: string, texto: string, autor: AuditoriaAtor): Promise<void> {
    const limpo = texto.trim();
    if (!limpo) throw new Error('O comentário está vazio.');
    const comentario: ComentarioAtivo = { id: crypto.randomUUID(), texto: limpo, autor, criadoEm: Timestamp.now() };
    await updateDoc(doc(db, this.collectionName, id), {
      comentarios: arrayUnion(comentario),
      updatedBy: autor,
      ultimaAlteracao: ultimaAlteracaoManual(autor),
      updatedAt: serverTimestamp(),
    });
  }

  /** Sobe o arquivo e registra na ficha (o histórico registra quem anexou). */
  /** `dono` só no "Termo": de quem é (null = termo antigo de outra pessoa, ninguém vê). */
  async adicionarAnexo(
    id: string,
    arquivo: File,
    categoria: CategoriaAnexoAtivo,
    autor: AuditoriaAtor,
    dono: { id: string; name: string } | null = null
  ): Promise<void> {
    const anexoId = crypto.randomUUID();
    const caminho = `si_equipamentos/${id}/anexos/${anexoId}-${arquivo.name}`;
    const url = await uploadFile(arquivo, caminho);
    const anexo: AnexoAtivo = {
      id: anexoId,
      nome: arquivo.name,
      url,
      caminho,
      categoria,
      autor,
      criadoEm: Timestamp.now(),
      ...(categoria === 'termo' ? { colaboradorId: dono?.id ?? null, colaboradorNome: dono?.name ?? null } : {}),
    };
    await updateDoc(doc(db, this.collectionName, id), {
      anexos: arrayUnion(anexo),
      updatedBy: autor,
      ultimaAlteracao: ultimaAlteracaoManual(autor),
      updatedAt: serverTimestamp(),
    });
  }

  /** Tira da ficha (anexado por engano). O arquivo continua no Storage e a remoção fica no
   * histórico — nada some sem rastro. */
  async removerAnexo(id: string, anexo: AnexoAtivo, autor: AuditoriaAtor): Promise<void> {
    await updateDoc(doc(db, this.collectionName, id), {
      anexos: arrayRemove(anexo),
      updatedBy: autor,
      ultimaAlteracao: ultimaAlteracaoManual(autor),
      updatedAt: serverTimestamp(),
    });
  }

  async getById(id: string): Promise<Equipamento | null> {
    const snap = await getDoc(doc(db, this.collectionName, id));
    if (!snap.exists()) {
      return null;
    }
    return this.paraEquipamento(snap);
  }

  /** Busca pelo patrimônio normalizado; se não achar, tenta o texto exato digitado —
   * cadastros antigos podem ter sido gravados antes da normalização existir. */
  async getByPatrimonio(patrimonio: string): Promise<Equipamento | null> {
    const normalizado = normalizarPatrimonio(patrimonio);
    const candidatos = Array.from(new Set([normalizado, patrimonio].filter((v): v is string => Boolean(v))));
    for (const valor of candidatos) {
      const snap = await getDocs(query(collection(db, this.collectionName), where('patrimonio', '==', valor)));
      // Excluído não conta — o patrimônio fica livre pra um cadastro novo.
      const ativo = snap.docs.find((d) => !d.data().excluido);
      if (ativo) return this.paraEquipamento(ativo);
    }
    return null;
  }

  /**
   * Cadastros que já usam este patrimônio (normalizado ou exato) ou este nº de série —
   * usado ao cadastrar na aba Ativos pra abrir o existente em vez de duplicar.
   * `ignorarId` = o próprio ativo sendo editado.
   */
  async buscarDuplicados(
    patrimonio: string | null | undefined,
    nSerie: string | null | undefined,
    ignorarId?: string
  ): Promise<Equipamento[]> {
    const consultas: Array<[string, string]> = [];
    const normalizado = normalizarPatrimonio(patrimonio);
    if (normalizado) consultas.push(['patrimonio', normalizado]);
    if (patrimonio && patrimonio !== normalizado) consultas.push(['patrimonio', patrimonio]);
    const serie = nSerie?.trim();
    if (serie) consultas.push(['nSerie', serie]);

    const encontrados = new Map<string, Equipamento>();
    for (const [campo, valor] of consultas) {
      const snap = await getDocs(query(collection(db, this.collectionName), where(campo, '==', valor)));
      snap.docs.forEach((d) => {
        if (d.id !== ignorarId && !d.data().excluido) encontrados.set(d.id, this.paraEquipamento(d));
      });
    }
    return Array.from(encontrados.values());
  }

  /** Lista para a tela "Equipamentos" (quem está com o quê) — filtro opcional por status. */
  async listar(filtro?: { statusCustodia?: StatusCustodiaEquipamento }): Promise<Equipamento[]> {
    const restricoes = filtro?.statusCustodia ? [where('statusCustodia', '==', filtro.statusCustodia)] : [];
    const snap = await getDocs(query(collection(db, this.collectionName), ...restricoes));
    return snap.docs.filter((d) => !d.data().excluido).map((d) => this.paraEquipamento(d));
  }
}

export const equipamentoService = new EquipamentoServiceClass();
